import { useEffect, useState } from "react";
import { usePlayer } from "@/contexts/PlayerContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Play, Pause, Stop, X, Heart, Broadcast, MusicNotes } from "@phosphor-icons/react";
import { toast } from "sonner";

function fmtTime(s) {
  if (!s || !Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export default function NowPlaying() {
  const { current, mode, isPlaying, toggle, stop, setExpanded, sleepEndsAt, progress, duration, seek } = usePlayer();
  const { user } = useAuth();
  const [isFav, setIsFav] = useState(false);
  const [countdown, setCountdown] = useState("");

  useEffect(() => {
    if (!sleepEndsAt) { setCountdown(""); return; }
    const tick = () => {
      const ms = sleepEndsAt - Date.now();
      if (ms <= 0) { setCountdown(""); return; }
      const m = Math.floor(ms / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setCountdown(`${m}:${s.toString().padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sleepEndsAt]);

  useEffect(() => {
    if (!current || !user) return;
    const ytTrackId = mode === "yt-audio" ? `youtube:${current.video_id}` : null;
    if (ytTrackId) {
      api.get("/favorites/tracks").then(({ data }) => {
        setIsFav(Boolean((data || []).find(x => x.track_id === ytTrackId)));
      }).catch(() => {});
      return;
    }
    const path =
      mode === "radio" ? "/favorites/radios"
      : mode === "video" ? "/favorites/videos"
      : "/favorites/tracks";
    const key = mode === "radio" ? "stationuuid" : mode === "video" ? "video_id" : "track_id";
    const itemId = current[key];
    api.get(path).then(({ data }) => {
      setIsFav(Boolean((data || []).find(x => x[key] === itemId)));
    }).catch(() => {});
  }, [current, mode, user]);

  if (!current) return null;
  const isVideo = mode === "video";
  const isTrack = mode === "track";
  const isYTAudio = mode === "yt-audio";
  const isMP3orYT = isTrack || isYTAudio;

  const toggleFav = async () => {
    if (!user) { toast.error("Please sign in to save favorites"); return; }
    try {
      if (isVideo) {
        if (isFav) await api.delete(`/favorites/videos/${current.video_id}`);
        else await api.post("/favorites/videos", {
          video_id: current.video_id, title: current.title,
          thumbnail: current.thumbnail, channel: current.channel,
        });
      } else if (isYTAudio) {
        const trackId = `youtube:${current.video_id}`;
        if (isFav) await api.delete(`/favorites/tracks/${encodeURIComponent(trackId)}`);
        else await api.post("/favorites/tracks", {
          track_id: trackId, source: "youtube",
          title: current.title, artist: current.channel || "",
          album: "", artwork: current.thumbnail || "",
          stream_url: "", duration: 0,
        });
      } else if (isTrack) {
        if (isFav) await api.delete(`/favorites/tracks/${encodeURIComponent(current.track_id)}`);
        else await api.post("/favorites/tracks", current);
      } else {
        if (isFav) await api.delete(`/favorites/radios/${current.stationuuid}`);
        else await api.post("/favorites/radios", {
          stationuuid: current.stationuuid, name: current.name,
          url: current.url_resolved || current.url, favicon: current.favicon || "",
          country: current.country || "", tags: current.tags || "",
        });
      }
      setIsFav(!isFav);
      toast.success(isFav ? "Removed" : "Saved to favorites");
    } catch (_) { toast.error("Action failed"); }
  };

  const title = isVideo || isTrack || isYTAudio ? current.title : current.name;
  const subtitle = isVideo
    ? current.channel
    : isTrack
      ? `${current.artist || "Unknown"}${current.album ? " · " + current.album : ""}`
      : isYTAudio
        ? `${current.channel || "YouTube"} · YouTube`
        : `${current.country || "Worldwide"}${current.bitrate ? ` · ${current.bitrate} kbps` : ""}`;
  const kindLabel = isVideo
    ? "Music Video"
    : isTrack
      ? `Free MP3 · ${current.source === "archive" ? "Archive" : "Jamendo"}`
      : isYTAudio
        ? "YouTube Music"
        : "Radio Station";

  return (
    <div
      data-testid="now-playing-overlay"
      className="fixed inset-0 z-[60] bg-[#1F2924] text-[#F4F1EA] overflow-auto"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 sm:pt-8 pb-32 sm:pb-40">
        <div className="flex items-center justify-between mb-8 sm:mb-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#D35230] flex items-center gap-3">
            <span className="w-8 h-px bg-[#D35230]" /> Now Playing
          </div>
          <button
            data-testid="now-playing-close"
            onClick={() => setExpanded(false)}
            className="w-11 h-11 border-2 border-[#F4F1EA]/30 hover:border-[#D35230] hover:bg-[#D35230] flex items-center justify-center"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 sm:gap-10 items-start">
          <div className="lg:col-span-7">
            {isVideo ? (
              <div className="aspect-video bg-black border-2 border-[#D35230]">
                <iframe
                  title="yt-fs"
                  width="100%" height="100%"
                  src={`https://www.youtube.com/embed/${current.video_id}?autoplay=1`}
                  frameBorder="0"
                  allow="autoplay; encrypted-media; fullscreen"
                  allowFullScreen
                />
              </div>
            ) : isTrack || isYTAudio ? (
              <div className="aspect-square max-w-md mx-auto lg:mx-0 bg-[#F4F1EA]/5 border-2 border-[#D35230] overflow-hidden relative">
                {(isTrack && current.artwork) || (isYTAudio && current.thumbnail) ? (
                  <img
                    src={isTrack ? current.artwork : current.thumbnail}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <MusicNotes size={120} color="#D35230" weight="duotone" />
                  </div>
                )}
                {isPlaying && (
                  <div className="absolute bottom-6 left-6 flex items-end gap-[6px] h-10">
                    {[0,1,2,3,4].map(i => (<div key={i} className="freq-bar w-2 bg-[#D35230]" style={{ height: "100%" }} />))}
                  </div>
                )}
                {isYTAudio && (
                  <div className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 bg-[#B91C1C] text-[#F4F1EA]">
                    YouTube · audio
                  </div>
                )}
              </div>
            ) : (
              <div className="aspect-square max-w-md mx-auto lg:mx-0 bg-[#F4F1EA]/5 border-2 border-[#F4F1EA]/20 flex items-center justify-center relative">
                {current.favicon ? (
                  <img
                    src={current.favicon}
                    alt=""
                    className="w-2/3 h-2/3 object-contain"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <Broadcast size={120} color="#D35230" weight="duotone" />
                )}
                {isPlaying && (
                  <div className="absolute bottom-8 left-8 flex items-end gap-[6px] h-12">
                    {[0,1,2,3,4].map(i => (
                      <div key={i} className="freq-bar w-2 bg-[#D35230]" style={{ height: "100%" }} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-5 space-y-5 sm:space-y-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D35230] mb-3">
                {kindLabel}
              </div>
              <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mb-2 sm:mb-3">
                {title}
              </h1>
              <div className="font-sans text-sm sm:text-base text-[#F4F1EA]/70">{subtitle}</div>
            </div>

            {/* Progress for tracks + yt-audio */}
            {isMP3orYT && duration > 0 && (
              <div>
                <div className="flex items-center justify-between font-mono text-[10px] text-[#F4F1EA]/60 mb-1.5 tabular-nums">
                  <span>{fmtTime(progress)}</span><span>{fmtTime(duration)}</span>
                </div>
                <input
                  data-testid="now-playing-progress"
                  type="range" min="0" max={duration} step="1"
                  value={Math.min(progress, duration)}
                  onChange={(e) => seek(parseFloat(e.target.value))}
                  className="w-full accent-[#D35230] h-1"
                />
              </div>
            )}

            {!isVideo && !isTrack && (current.tags || "").length > 0 && (
              <div className="flex flex-wrap gap-2">
                {(current.tags || "").split(",").filter(Boolean).slice(0, 6).map((t, i) => (
                  <span key={i} className="font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 border border-[#F4F1EA]/30">
                    {t.trim()}
                  </span>
                ))}
              </div>
            )}

            {isTrack && (current.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {(current.tags || []).slice(0, 6).map((t, i) => (
                  <span key={i} className="font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 border border-[#F4F1EA]/30">
                    {t}
                  </span>
                ))}
              </div>
            )}

            <div className="flex items-center gap-3 pt-1 sm:pt-2">
              {!isVideo && (
                <button
                  onClick={toggle}
                  className="w-14 h-14 sm:w-16 sm:h-16 border-2 border-[#F4F1EA] hover:bg-[#D35230] hover:border-[#D35230] flex items-center justify-center transition-colors"
                  data-testid="now-playing-toggle"
                  aria-label={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? <Pause size={24} weight="fill" /> : <Play size={24} weight="fill" />}
                </button>
              )}
              <button
                onClick={stop}
                className="w-12 h-12 border-2 border-[#F4F1EA]/40 hover:border-[#F4F1EA] flex items-center justify-center"
                data-testid="now-playing-stop"
                aria-label="Stop"
              >
                <Stop size={18} weight="fill" />
              </button>
              <button
                onClick={toggleFav}
                className="w-12 h-12 border-2 border-[#F4F1EA]/40 hover:border-[#D35230] flex items-center justify-center"
                data-testid="now-playing-fav"
                aria-label="Favorite"
              >
                <Heart size={18} weight={isFav ? "fill" : "regular"} color={isFav ? "#D35230" : "#F4F1EA"} />
              </button>
            </div>

            {countdown && (
              <div className="border-t border-[#F4F1EA]/15 pt-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D35230] mb-1">Sleep timer</div>
                <div className="font-mono text-2xl text-[#F4F1EA]">{countdown}</div>
              </div>
            )}

            <div className="border-t border-[#F4F1EA]/15 pt-4 sm:pt-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F4F1EA]/50 mb-2">Quote of the airwave</div>
              <p className="font-serif-display text-xl sm:text-2xl italic leading-relaxed text-[#F4F1EA]/80">
                {isVideo
                  ? "Listen across borders. Music has no passport."
                  : isTrack
                    ? "Free as the airwaves — Creative Commons, all the way through."
                    : isYTAudio
                      ? "From Mumbai to Madras to Manila — the world sings on YouTube."
                      : "Somewhere in the world, this signal travels through the night."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
